import type {
  ListOpsVpsResponse,
  OpsVpsConfigRecord,
  OpsVpsNodeSummary,
  OpsVpsTone,
  UpsertOpsVpsConfigRequest,
} from '@nadoutong/contracts';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Pencil, Plus, X } from 'lucide-react';
import { OpsApiError } from '../api/client.ts';
import {
  useCreateVpsConfig,
  useUpdateVpsConfig,
  useUpdateVpsAutomationSettings,
  useVpsAutomationSettings,
  useVpsConfigs,
  useVpsNodeDetail,
  useVpsNodes,
  useVpsLbAction,
} from '../api/vps.ts';
import type { VpsLbOutcome, VpsLbVerb } from '../api/vps.ts';
import { Modal } from '../components/Modal.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import {
  VpsTrendSparkline,
  clampRatio,
  computeDailyBurnRatios,
} from '../components/VpsTrendSparkline.tsx';
import type { VpsTrendSamplePoint } from '../components/VpsTrendSparkline.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import type { ChipTone } from '../ui/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  FormField,
  IconButton,
  Input,
  KpiCard,
  PageHeader,
  Panel,
  Switch,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import type { KpiTone } from '../ui/index.ts';

const BYTE_UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB'] as const;
const IPV4_PATTERN = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

type LbEntry = NonNullable<OpsVpsNodeSummary['lb']>[number];
type VpsNodeWithTrend = ListOpsVpsResponse['nodes'][number];

const VERB_KEYS: Record<VpsLbVerb, I18nKey> = {
  join: 'vps.lb.action.join',
  enable: 'vps.lb.action.enable',
  disable: 'vps.lb.action.disable',
  remove: 'vps.lb.action.remove',
};

const OUTCOME_KEYS: Record<VpsLbOutcome, I18nKey> = {
  joined: 'vps.lb.outcome.joined',
  updated: 'vps.lb.outcome.updated',
  enabled: 'vps.lb.outcome.enabled',
  disabled: 'vps.lb.outcome.disabled',
  removed: 'vps.lb.outcome.removed',
  absent: 'vps.lb.outcome.absent',
  held: 'vps.lb.outcome.held',
};

const PURPOSE_KEYS: Record<LbEntry['purpose'], I18nKey> = {
  router: 'vps.lb.purpose.router',
  sidecar: 'vps.lb.purpose.sidecar',
  platform: 'vps.lb.purpose.platform',
};

function lbChipState(entry: LbEntry): { tone: ChipTone; key: I18nKey } {
  if (!entry.inPool) return { tone: 'neutral', key: 'vps.lb.state.not_in_pool' };
  if (entry.enabled === true) return { tone: 'ok', key: 'vps.lb.state.enabled' };
  if (entry.autoDisabled) return { tone: 'bad', key: 'vps.lb.state.disabled_auto' };
  return { tone: 'warn', key: 'vps.lb.state.disabled_manual' };
}

function verbsFor(entry: LbEntry): readonly VpsLbVerb[] {
  if (!entry.inPool) return ['join'];
  if (entry.enabled === true) return ['disable', 'remove'];
  return ['enable', 'remove'];
}

export function VpsManagementPage(): JSX.Element {
  const t = useT();
  const query = useVpsNodes();
  const configsQuery = useVpsConfigs();

  return (
    <section className="flex flex-col gap-6" data-testid="vps-page">
      <PageHeader title={t('vps.title')} sub={t('vps.subtitle')} />

      <VpsAutomationSettingsCard />

      <VpsConfigPanel
        configs={configsQuery.data?.configs ?? []}
        isLoading={configsQuery.isLoading}
        isError={configsQuery.isError}
        error={configsQuery.error}
      />

      {query.isLoading ? (
        <p className="m-0 text-dense text-paper-low" data-testid="vps-loading">
          {t('vps.loading')}
        </p>
      ) : query.isError ? (
        <p className="m-0 text-dense text-bad" role="alert" data-testid="vps-error">
          {errorMessage(query.error)}
        </p>
      ) : query.data === undefined || query.data.nodes.length === 0 ? (
        <article
          className="rounded-lg border border-line-soft bg-card p-6 text-dense text-paper-low"
          data-testid="vps-empty"
        >
          {t('vps.empty')}
        </article>
      ) : (
        <>
          <SummaryGrid
            total={query.data.total}
            unhealthy={query.data.unhealthy}
            lowestTrafficRemainingRatio={query.data.lowestTrafficRemainingRatio}
            generatedAt={query.data.generatedAt}
          />
          <NodesTable nodes={query.data.nodes} />
        </>
      )}
    </section>
  );
}

const ALERT_THRESHOLD_MIN_PCT = 0.1;
const ALERT_THRESHOLD_MAX_PCT = 50;

function VpsAutomationSettingsCard(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const query = useVpsAutomationSettings();
  const updateSettings = useUpdateVpsAutomationSettings();
  const [seeded, setSeeded] = useState(false);
  const [pctInput, setPctInput] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!seeded && query.data !== undefined) {
      // Round the display seed too: a stored 0.011 would otherwise render as
      // "1.0999999999999999" (IEEE-754 noise in ratio * 100).
      setPctInput(String(Number((query.data.settings.alert_threshold_ratio * 100).toFixed(2))));
      setSeeded(true);
    }
  }, [seeded, query.data]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const pct = Number(pctInput);
    if (!Number.isFinite(pct) || pct < ALERT_THRESHOLD_MIN_PCT || pct > ALERT_THRESHOLD_MAX_PCT) {
      setValidationError(t('vps.automation.invalid'));
      return;
    }
    setValidationError(null);
    updateSettings.mutate(
      // Round away IEEE-754 division noise (1.1 / 100 = 0.011000000000000001)
      // so the persisted wire value stays clean at the input's 0.1% granularity.
      { alert_threshold_ratio: Number((pct / 100).toFixed(4)) },
      {
        onSuccess: () => toast.success(t('vps.automation.saved')),
        onError: (err: Error) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  return (
    <Panel caption={t('vps.automation.title')} padded testId="vps-automation-settings-panel">
      {query.isError ? (
        <p className="m-0 text-dense text-bad" role="alert" data-testid="vps-automation-error">
          {t('vps.automation.loadError')}
        </p>
      ) : (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex flex-col gap-3"
          data-testid="vps-automation-form"
        >
          <p className="m-0 text-dense text-paper-low">{t('vps.automation.hint')}</p>
          <div className="flex items-end gap-3">
            <FormField
              label={t('vps.automation.inputLabel')}
              fieldId="vps-automation-threshold-input"
              error={validationError}
              className="max-w-[160px]"
            >
              <Input
                id="vps-automation-threshold-input"
                type="number"
                step="0.1"
                min={ALERT_THRESHOLD_MIN_PCT}
                max={ALERT_THRESHOLD_MAX_PCT}
                value={pctInput}
                onChange={(event) => setPctInput(event.target.value)}
                data-testid="vps-automation-threshold-input"
              />
            </FormField>
            <Button
              type="submit"
              variant="primary"
              loading={updateSettings.isPending}
              data-testid="vps-automation-save"
            >
              {t('vps.automation.save')}
            </Button>
          </div>
        </form>
      )}
    </Panel>
  );
}

interface VpsConfigPanelProps {
  readonly configs: readonly OpsVpsConfigRecord[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
}

function VpsConfigPanel({
  configs,
  isLoading,
  isError,
  error,
}: VpsConfigPanelProps): JSX.Element {
  const t = useT();
  const [modalConfig, setModalConfig] = useState<OpsVpsConfigRecord | null | 'new'>(null);
  return (
    <Panel
      caption={t('vps.config.title')}
      padded
      testId="vps-configs-panel"
      actions={
        <Button
          type="button"
          variant="primary"
          data-testid="vps-config-add"
          onClick={() => setModalConfig('new')}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          {t('vps.config.add')}
        </Button>
      }
    >
      <p className="m-0 text-dense text-paper-low">{t('vps.config.subtitle')}</p>

      {isLoading ? (
        <p className="m-0 mt-4 text-dense text-paper-low">{t('vps.config.loading')}</p>
      ) : isError ? (
        <p className="m-0 mt-4 text-dense text-bad" role="alert">
          {errorMessage(error)}
        </p>
      ) : configs.length === 0 ? (
        <p className="m-0 mt-4 text-dense text-paper-low" data-testid="vps-configs-empty">
          {t('vps.config.empty')}
        </p>
      ) : (
        <Table className="mt-4">
          <THead>
            <Tr interactive={false}>
              <Th>{t('vps.config.column.veid')}</Th>
              <Th>{t('vps.config.column.key')}</Th>
              <Th>{t('vps.config.column.state')}</Th>
              <Th className="text-right">{t('vps.config.column.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {configs.map((config) => (
              <Tr key={config.id} data-testid={`vps-config-${config.id}`}>
                <Td mono emphasis>
                  {config.veid}
                </Td>
                <Td mono>
                  {config.keyPreview ??
                    (config.hasApiKey ? t('vps.config.key_configured') : t('vps.config.key_missing'))}
                </Td>
                <Td>
                  <Chip tone={config.enabled ? 'ok' : 'bad'}>
                    {config.enabled ? t('vps.config.enabled') : t('vps.config.disabled')}
                  </Chip>
                </Td>
                <Td className="text-right">
                  <IconButton
                    size="sm"
                    data-testid={`vps-config-edit-${config.id}`}
                    onClick={() => setModalConfig(config)}
                    label={`${t('vps.config.edit')} ${config.veid}`}
                  >
                    <Pencil aria-hidden="true" className="h-4 w-4" />
                  </IconButton>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {modalConfig !== null ? (
        <VpsConfigModal
          config={modalConfig === 'new' ? null : modalConfig}
          onClose={() => setModalConfig(null)}
        />
      ) : null}
    </Panel>
  );
}

function VpsConfigModal({
  config,
  onClose,
}: {
  readonly config: OpsVpsConfigRecord | null;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const createConfig = useCreateVpsConfig();
  const updateConfig = useUpdateVpsConfig();
  const isEdit = config !== null;
  const pending = createConfig.isPending || updateConfig.isPending;
  const [deployTarget, setDeployTarget] = useState<boolean>(config?.deployTarget ?? true);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const fd = new FormData(event.currentTarget);
    const veid = stringField(fd, 'veid');
    const apiKey = stringField(fd, 'api_key');
    const deployIp = stringField(fd, 'deploy_ip');
    if (!isEdit && veid.length === 0) {
      toast.error(t('vps.config.toast.veid_required'));
      return;
    }
    if (!isEdit && apiKey.length === 0) {
      toast.error(t('vps.config.toast.key_required'));
      return;
    }
    if (deployTarget && deployIp.length > 0 && !IPV4_PATTERN.test(deployIp)) {
      toast.error(t('vps.config.toast.deploy_ip_invalid'));
      return;
    }
    const deployFields = {
      deploy_target: deployTarget,
      deploy_ip: deployIp.length > 0 ? deployIp : null,
    };
    const payload: UpsertOpsVpsConfigRequest = isEdit
      ? apiKey.length > 0
        ? { api_key: apiKey, ...deployFields }
        : deployFields
      : { veid, api_key: apiKey, ...deployFields };
    const handlers = {
      onSuccess: () => {
        toast.success(t('vps.config.toast.saved'));
        onClose();
      },
      onError: (err: Error) => toast.error(coerceErrorMessage(err)),
    };
    if (isEdit) {
      updateConfig.mutate({ id: config.id, payload }, handlers);
      return;
    }
    createConfig.mutate(payload, handlers);
  }

  return (
    <Modal isOpen onClose={onClose} titleId="vps-config-modal-title" testId="vps-config-modal">
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5 p-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h3 id="vps-config-modal-title" className="m-0 text-title text-paper">
              {isEdit ? t('vps.config.modal.edit_title') : t('vps.config.modal.add_title')}
            </h3>
            {isEdit ? (
              <p className="m-0 mt-1 font-mono text-label text-paper-low">
                {config.veid} · {config.keyPreview ?? t('vps.config.key_configured')}
              </p>
            ) : null}
          </div>
          <IconButton size="sm" onClick={onClose} label={t('common.close')}>
            <X aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </header>

        <FormField label={t('vps.config.field.veid')} fieldId="vps-config-veid-input">
          <Input
            id="vps-config-veid-input"
            name="veid"
            mono
            defaultValue={config?.veid ?? ''}
            disabled={isEdit}
            data-testid="vps-config-veid-input"
            autoComplete="off"
          />
        </FormField>

        <FormField label={t('vps.config.field.deploy_ip')} fieldId="vps-config-deploy-ip-input">
          <Input
            id="vps-config-deploy-ip-input"
            name="deploy_ip"
            mono
            defaultValue={config?.deployIp ?? ''}
            data-testid="vps-config-deploy-ip-input"
            autoComplete="off"
          />
        </FormField>

        <Switch
          checked={deployTarget}
          onCheckedChange={setDeployTarget}
          label={t('vps.config.field.deploy_target')}
          testId="vps-config-deploy-target-input"
        />

        <FormField
          label={isEdit ? t('vps.config.field.rotate_api_key') : t('vps.config.field.api_key')}
          fieldId="vps-config-api-key-input"
        >
          <Input
            id="vps-config-api-key-input"
            name="api_key"
            type="password"
            mono
            data-testid="vps-config-api-key-input"
            autoComplete="off"
          />
        </FormField>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('vps.config.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={pending} data-testid="vps-config-save">
            {t('vps.config.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function stringField(fd: FormData, name: string): string {
  const value = fd.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

interface SummaryGridProps {
  readonly total: number;
  readonly unhealthy: number;
  readonly lowestTrafficRemainingRatio: number | null;
  readonly generatedAt: string;
}

function SummaryGrid({
  total,
  unhealthy,
  lowestTrafficRemainingRatio,
  generatedAt,
}: SummaryGridProps): JSX.Element {
  const t = useT();
  return (
    <div className="grid gap-4 md:grid-cols-4" data-testid="vps-summary">
      <KpiCard label={t('vps.metric.nodes')} value={String(total)} delta={{ text: t('vps.metric.nodes_detail'), tone: 'neutral' }} />
      <KpiCard
        label={t('vps.metric.unhealthy')}
        value={String(unhealthy)}
        delta={{ text: t('vps.metric.unhealthy_detail'), tone: 'neutral' }}
        tone={unhealthy > 0 ? 'warn' : 'ok'}
        lamp={unhealthy > 0 ? 'warn' : 'ok'}
      />
      <KpiCard
        label={t('vps.metric.lowest_traffic')}
        value={formatNullablePercent(lowestTrafficRemainingRatio)}
        delta={{ text: t('vps.metric.lowest_traffic_detail'), tone: 'neutral' }}
        tone={vpsToneToKpiTone(
          lowestTrafficRemainingRatio !== null && lowestTrafficRemainingRatio < 0.1
            ? 'critical'
            : lowestTrafficRemainingRatio !== null && lowestTrafficRemainingRatio < 0.2
              ? 'warning'
              : 'healthy',
        )}
      />
      <KpiCard
        label={t('vps.metric.updated')}
        value={formatLocalTimestamp(generatedAt, { seconds: true })}
        delta={{ text: t('vps.metric.updated_detail'), tone: 'neutral' }}
      />
    </div>
  );
}

function NodesTable({ nodes }: { readonly nodes: readonly VpsNodeWithTrend[] }): JSX.Element {
  const t = useT();
  const [detailNode, setDetailNode] = useState<VpsNodeWithTrend | null>(null);
  const enabledCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const node of nodes) {
      const lb = node.lb ?? [];
      for (const entry of lb) {
        if (entry.inPool && entry.enabled === true) {
          counts.set(entry.poolId, (counts.get(entry.poolId) ?? 0) + 1);
        }
      }
    }
    return counts;
  }, [nodes]);
  return (
    <>
      <Table testId="vps-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('vps.column.node')}</Th>
            <Th>{t('vps.column.status')}</Th>
            <Th>{t('vps.column.plan')}</Th>
            <Th num>{t('vps.column.traffic')}</Th>
            <Th>{t('vps.column.trend')}</Th>
            <Th num>{t('vps.column.billing_cycle')}</Th>
            <Th num>{t('vps.column.load')}</Th>
            <Th num>{t('vps.column.disk')}</Th>
            <Th>{t('vps.column.risks')}</Th>
            <Th>{t('vps.column.lb')}</Th>
          </Tr>
        </THead>
        <TBody>
          {nodes.map((node) => (
            <NodeRow key={node.id} node={node} enabledCounts={enabledCounts} onOpenDetail={setDetailNode} />
          ))}
        </TBody>
      </Table>
      {detailNode !== null ? (
        <VpsNodeDetailDialog node={detailNode} onClose={() => setDetailNode(null)} />
      ) : null}
    </>
  );
}

function NodeRow({
  node,
  enabledCounts,
  onOpenDetail,
}: {
  readonly node: VpsNodeWithTrend;
  readonly enabledCounts: ReadonlyMap<string, number>;
  readonly onOpenDetail: (node: VpsNodeWithTrend) => void;
}): JSX.Element {
  const t = useT();
  const burn = node.trend.burn24hBytesPerDay ?? node.trend.burn7dBytesPerDay;
  return (
    <Tr className="align-top" data-testid={`vps-row-${node.id}`} data-tone={node.tone}>
      <Td className="align-top">
        <div className="flex flex-col gap-1">
          <span className="font-medium text-paper">{node.label}</span>
          <span className="font-mono text-micro text-paper-low">
            {node.environment} · {node.role}
          </span>
          {node.publicHost !== null ? (
            <span className="font-mono text-micro text-paper-low">{node.publicHost}</span>
          ) : null}
        </div>
      </Td>
      <Td className="align-top">
        <Chip tone={vpsToneToChipTone(node.tone)}>{node.statusLabel}</Chip>
      </Td>
      <Td className="align-top">
        <div className="flex flex-col gap-1">
          <span className="text-paper">{node.plan.name}</span>
          <span className="text-micro text-paper-low">
            {[node.plan.datacenter, node.plan.nodeAlias, node.plan.operatingSystem]
              .filter((part): part is string => part !== null && part.length > 0)
              .join(' · ') || '—'}
          </span>
        </div>
      </Td>
      <Td num className="align-top">
        <div className="flex flex-col gap-1">
          <span className="text-paper">{formatNullablePercent(node.traffic.usedRatio)}</span>
          <span className="text-paper-low">
            {formatBytes(node.traffic.remainingBytes)} / {formatBytes(node.traffic.monthlyBytes)}
          </span>
        </div>
      </Td>
      <Td className="align-top">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="block w-fit rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            data-testid={`vps-node-detail-open-${node.id}`}
            aria-label={t('vps.trend.open_detail', { label: node.label })}
            onClick={() => onOpenDetail(node)}
          >
            <VpsTrendSparkline
              samples={node.trend.samples7d}
              testId={`vps-trend-sparkline-${node.id}`}
              label={t('vps.trend.sparkline_aria', { label: node.label })}
              emptyLabel={t('vps.trend.no_samples')}
            />
          </button>
          <span className="text-micro text-paper-low" data-testid={`vps-trend-burn-${node.id}`}>
            {burn !== null
              ? t('vps.trend.burn_per_day', { value: formatBytes(burn) })
              : t('vps.trend.no_burn')}
          </span>
          <span className="text-micro text-paper-low" data-testid={`vps-trend-projection-${node.id}`}>
            {node.trend.projectedExhaustionAt !== null
              ? t('vps.trend.projected', { date: formatLocalTimestamp(node.trend.projectedExhaustionAt) })
              : t('vps.trend.no_projection')}
          </span>
        </div>
      </Td>
      <Td num className="align-top">
        <div className="flex flex-col gap-1">
          <span className="text-paper">{formatLocalTimestamp(node.traffic.resetAt)}</span>
          <span className="text-paper-low">{t('vps.billing_cycle.reset_hint')}</span>
        </div>
      </Td>
      <Td num emphasis className="align-top">
        {node.resources.loadAverage.length > 0 ? node.resources.loadAverage.join(' / ') : '—'}
      </Td>
      <Td num emphasis className="align-top">
        {formatBytes(node.resources.diskUsedBytes)} / {formatBytes(node.resources.diskTotalBytes)}
      </Td>
      <Td className="align-top">
        <div className="flex max-w-[280px] flex-wrap gap-1">
          {node.risks.length === 0 ? (
            <span className="text-micro text-paper-low">—</span>
          ) : (
            node.risks.map((risk) => (
              <span
                key={risk.id}
                title={risk.detail}
                data-testid={`vps-risk-${node.id}-${risk.id}`}
              >
                <Chip tone={vpsToneToChipTone(risk.tone)} lamp={false}>
                  {risk.label}
                </Chip>
              </span>
            ))
          )}
        </div>
      </Td>
      <Td className="align-top">
        <NodeLbCell node={node} enabledCounts={enabledCounts} />
      </Td>
    </Tr>
  );
}

function VpsNodeDetailDialog({
  node,
  onClose,
}: {
  readonly node: VpsNodeWithTrend;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const detailQuery = useVpsNodeDetail(node.id);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={node.label}
      description={t('vps.detail.subtitle')}
      size="xl"
      closeLabel={t('common.close')}
      testId="vps-node-detail-dialog"
    >
      {detailQuery.isLoading ? (
        <p className="m-0 text-dense text-paper-low" data-testid="vps-detail-loading">
          {t('vps.detail.loading')}
        </p>
      ) : detailQuery.isError ? (
        <p className="m-0 text-dense text-bad" role="alert" data-testid="vps-detail-error">
          {errorMessage(detailQuery.error)}
        </p>
      ) : detailQuery.data === undefined || detailQuery.data.samples30d.length < 2 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="vps-detail-empty">
          {t('vps.detail.empty')}
        </p>
      ) : (
        <DetailTrendChart samples={detailQuery.data.samples30d} />
      )}
    </Dialog>
  );
}

/** 30d chart — same geometry as TrafficMonitorPage TrendPanel (:379-389). */
function DetailTrendChart({
  samples,
}: {
  readonly samples: readonly VpsTrendSamplePoint[];
}): JSX.Element {
  const t = useT();
  const width = 920;
  const height = 220;
  const paddingX = 24;
  const bottomY = 168;
  const chartHeight = 132;
  const dailyBurn = computeDailyBurnRatios(samples);
  const maxBurn = Math.max(0.01, ...dailyBurn.map((day) => day.burnedRatio));
  const step = samples.length > 1 ? (width - paddingX * 2) / (samples.length - 1) : 0;
  const linePoints = samples
    .map((sample, index) => {
      const x = paddingX + step * index;
      const y = bottomY - clampRatio(sample.remainingRatio) * chartHeight;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const groupWidth = Math.max(8, (width - paddingX * 2) / Math.max(1, dailyBurn.length));
  const labelEvery = Math.max(1, Math.ceil(dailyBurn.length / 7));
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3 text-label text-paper-low">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-2 w-2 rounded-sm bg-signal" />
          {t('vps.detail.legend.remaining')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-2 w-2 rounded-sm bg-warn" />
          {t('vps.detail.legend.daily_burn')}
        </span>
      </div>
      <div className="overflow-x-auto [scrollbar-gutter:stable]">
        <svg
          role="img"
          aria-label={t('vps.detail.subtitle')}
          data-testid="vps-detail-trend-chart"
          viewBox={`0 0 ${width} ${height}`}
          className="block h-56 min-w-[640px] rounded-lg bg-well"
        >
          <line x1={paddingX} x2={width - paddingX} y1={bottomY} y2={bottomY} strokeWidth={1} className="stroke-line" />
          {dailyBurn.map((day, index) => {
            const x = paddingX + index * groupWidth + groupWidth * 0.2;
            const barWidth = Math.max(3, groupWidth * 0.55);
            const barHeight = Math.max(2, Math.round((day.burnedRatio / maxBurn) * chartHeight));
            return (
              <g key={day.day} data-testid={`vps-detail-burn-bar-${index}`}>
                <title>{`${day.day} · ${formatNullablePercent(day.burnedRatio)}`}</title>
                <rect x={x} y={bottomY - barHeight} width={barWidth} height={barHeight} rx={1} className="fill-warn" />
                {index % labelEvery === 0 || index === dailyBurn.length - 1 ? (
                  <text x={x + barWidth / 2} y={198} textAnchor="middle" className="fill-paper-low font-mono text-micro">
                    {day.day.slice(5)}
                  </text>
                ) : null}
              </g>
            );
          })}
          {samples.length > 1 ? (
            <polyline
              points={linePoints}
              fill="none"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-signal"
            />
          ) : null}
        </svg>
      </div>
    </div>
  );
}

function NodeLbCell({
  node,
  enabledCounts,
}: {
  readonly node: OpsVpsNodeSummary;
  readonly enabledCounts: ReadonlyMap<string, number>;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const action = useVpsLbAction();
  const [pending, setPending] = useState<{ entry: LbEntry; verb: VpsLbVerb } | null>(null);
  const lb = node.lb ?? [];

  if (lb.length === 0) {
    return (
      <span className="text-micro text-paper-low" data-testid={`vps-lb-empty-${node.id}`}>
        {t('vps.lb.empty')}
      </span>
    );
  }

  const floorWarning =
    pending !== null &&
    (pending.verb === 'disable' || pending.verb === 'remove') &&
    pending.entry.enabled === true &&
    (enabledCounts.get(pending.entry.poolId) ?? 0) <= 2;

  function confirm(): void {
    if (pending === null) return;
    action.mutate(
      { nodeId: node.id, poolId: pending.entry.poolId, verb: pending.verb },
      {
        onSuccess: (res) => {
          toast.success(t(OUTCOME_KEYS[res.outcome]));
          setPending(null);
        },
        onError: (err: Error) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {lb.map((entry) => {
        const state = lbChipState(entry);
        return (
          <div key={entry.poolId} className="flex flex-wrap items-center gap-1.5">
            <Chip tone={state.tone} lamp={false} testId={`vps-lb-chip-${node.id}-${entry.poolId}`}>
              {t(PURPOSE_KEYS[entry.purpose])} · {t(state.key)}
            </Chip>
            {verbsFor(entry).map((verb) => (
              <Button
                key={verb}
                type="button"
                size="sm"
                variant={verb === 'remove' ? 'danger' : 'ghost'}
                data-testid={`vps-lb-${verb}-${node.id}-${entry.poolId}`}
                onClick={() => setPending({ entry, verb })}
              >
                {t(VERB_KEYS[verb])}
              </Button>
            ))}
          </div>
        );
      })}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !action.isPending) setPending(null);
        }}
        title={pending !== null ? t(VERB_KEYS[pending.verb]) : ''}
        body={
          pending !== null ? (
            <div className="flex flex-col gap-2">
              <p className="m-0">
                {t('vps.lb.confirm.body', {
                  node: node.label,
                  pool: t(PURPOSE_KEYS[pending.entry.purpose]),
                  action: t(VERB_KEYS[pending.verb]),
                })}
              </p>
              <p className="m-0 font-mono text-micro text-paper-low">{pending.entry.poolId}</p>
              {floorWarning ? (
                <p className="m-0 text-dense text-warn" data-testid="vps-lb-floor-warning">
                  ⚠ {t('vps.lb.confirm.floor_warning')}
                </p>
              ) : null}
            </div>
          ) : null
        }
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={confirm}
        loading={action.isPending}
        testId="vps-lb-confirm"
      />
    </div>
  );
}

function formatBytes(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  let scaled = Math.max(0, value);
  let unitIndex = 0;
  while (scaled >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    scaled /= 1024;
    unitIndex += 1;
  }
  const fractionDigits = scaled >= 100 || unitIndex === 0 ? 0 : 1;
  return `${scaled.toFixed(fractionDigits)} ${BYTE_UNITS[unitIndex]}`;
}

function formatNullablePercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function vpsToneToChipTone(tone: OpsVpsTone): ChipTone {
  if (tone === 'critical') return 'bad';
  if (tone === 'warning') return 'warn';
  if (tone === 'healthy') return 'ok';
  return 'neutral';
}

function vpsToneToKpiTone(tone: OpsVpsTone): KpiTone {
  if (tone === 'critical') return 'bad';
  if (tone === 'warning') return 'warn';
  if (tone === 'healthy') return 'ok';
  return 'default';
}

function errorMessage(error: unknown): string {
  if (error instanceof OpsApiError) return error.message;
  return error instanceof Error ? error.message : String(error);
}
